import {Table, TableRow} from 'platform/components';
import {Box, Text} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {isNil} from 'ramda';

import {GetDeliveryNoteResponse, GetReceiveNoteItemsTotalPriceResponse} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

import {Nullish, RequiredTestIdProps} from 'shared/web';

interface TotalsTabProps extends RequiredTestIdProps {
  deliveryNote: GetDeliveryNoteResponse | Nullish;
  receiveNoteTotalPrice: GetReceiveNoteItemsTotalPriceResponse | Nullish;
  currency: string | Nullish;
}

const PRECISION = 2;

export function TotalsTab(props: TotalsTabProps) {
  const formatCurrency = useFormatCurrency();

  const currency = props.currency ?? 'CZK';

  const deliveryNotePriceWithoutVat = props.deliveryNote?.totalPrice?.withoutVat?.amount ?? 0;
  const deliveryNotePriceWithVat = props.deliveryNote?.totalPrice?.withVat?.amount ?? 0;

  const receiveNotePriceWithoutVat =
    !isNil(props.receiveNoteTotalPrice) && 'totalPriceWithoutVat' in props.receiveNoteTotalPrice
      ? props.receiveNoteTotalPrice.totalPriceWithoutVat
      : 0;

  const receiveNotePriceWithVat =
    !isNil(props.receiveNoteTotalPrice) && 'totalPriceWithVat' in props.receiveNoteTotalPrice
      ? props.receiveNoteTotalPrice.totalPriceWithVat
      : 0;

  const checkingTotalsWithoutVat = receiveNotePriceWithoutVat - deliveryNotePriceWithoutVat;
  const checkingTotalsWithVat = receiveNotePriceWithVat - deliveryNotePriceWithVat;

  const hasDiscrepancy = checkingTotalsWithoutVat !== 0 || checkingTotalsWithVat !== 0;

  const formatPrice = (amount: number) => formatCurrency(amount, currency, PRECISION);

  return (
    <Table>
      <TableRow actions={{primary: []}}>
        <Box padding={3}>
          <Text> {i18n.t('general.labels.type')}</Text>
        </Box>
        <Box padding={3}>
          <Text>{i18n.t('entity.warehouse.labels.totalPriceWithoutVat')}</Text>
        </Box>
        <Box padding={3}>
          <Text>{i18n.t('entity.warehouse.labels.totalPriceWithVat')}</Text>
        </Box>
      </TableRow>

      <TableRow actions={{primary: []}}>
        <Box padding={3}>
          <Text>{i18n.t('entity.warehouse.labels.deliveryNote')}</Text>
        </Box>
        <Box padding={3}>
          <Text>{formatPrice(deliveryNotePriceWithoutVat)}</Text>
        </Box>
        <Box padding={3}>
          <Text>{formatPrice(deliveryNotePriceWithVat)}</Text>
        </Box>
      </TableRow>

      <TableRow actions={{primary: []}}>
        <Box padding={3}>
          <Text>{i18n.t('entity.warehouse.labels.receiveNote')}</Text>
        </Box>
        <Box padding={3}>
          <Text>{formatPrice(receiveNotePriceWithoutVat)}</Text>
        </Box>
        <Box padding={3}>
          <Text>{formatPrice(receiveNotePriceWithVat)}</Text>
        </Box>
      </TableRow>

      <TableRow
        color={hasDiscrepancy ? 'palettes.red.10.100' : 'palettes.green.10.100'}
        actions={{primary: []}}
      >
        <Box padding={3}>
          <Text color={hasDiscrepancy ? 'danger' : 'success'}>
            {i18n.t('entity.warehouse.labels.checkingTotals')}
          </Text>
        </Box>
        <Box padding={3}>
          <Text color={hasDiscrepancy ? 'danger' : 'success'}>
            {formatPrice(checkingTotalsWithoutVat)}
          </Text>
        </Box>
        <Box padding={3}>
          <Text color={hasDiscrepancy ? 'danger' : 'success'}>
            {formatPrice(checkingTotalsWithVat)}
          </Text>
        </Box>
      </TableRow>
    </Table>
  );
}
